import {
  Box,
  Button,
  Card,
  Divider,
  Group,
  Image,
  Select,
  SegmentedControl,
  Stack,
  Switch,
  Tabs,
  Text,
} from '@mantine/core';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import PaletteColourField from 'src/secret-menu/brand-asset-builder/components/PaletteColourField';
import PreviewCanvas from 'src/secret-menu/brand-asset-builder/components/PreviewCanvas';
import { useGoogleFont } from 'src/secret-menu/brand-asset-builder/hooks/useGoogleFont';
import { useHexColourDraft } from 'src/secret-menu/brand-asset-builder/hooks/useHexColourDraft';
import { usePaletteColourDrafts } from 'src/secret-menu/brand-asset-builder/hooks/usePaletteColourDrafts';
import {
  BULLET_STYLE_GLYPHS,
  BULLET_STYLE_OPTIONS,
} from 'src/secret-menu/brand-asset-builder/lib/bulletStyles';
import { contrastTextColour, lightTint } from 'src/secret-menu/brand-asset-builder/lib/hexColour';
import {
  DEFAULT_BRAND_ASSET_BUILDER_PALETTE,
  type BrandAssetBuilderBulletStyle,
  type BrandAssetBuilderData,
  type BrandAssetBuilderPalette,
} from 'src/secret-menu/brand-asset-builder/types';

const HEADING_FONT_OPTIONS = [
  'Aptos Display',
  'Arial',
  'Calibri',
  'Helvetica',
  'Times New Roman',
  'Poppins',
  'Montserrat',
  'Roboto',
  'Open Sans',
  'Lato',
];

const BODY_FONT_OPTIONS = [
  'Aptos',
  'Arial',
  'Calibri',
  'Helvetica',
  'Times New Roman',
  'Poppins',
  'Montserrat',
  'Roboto',
  'Open Sans',
  'Lato',
];

const PALETTE_FIELDS: {
  key: keyof BrandAssetBuilderPalette;
  label: string;
  description: string;
}[] = [
  { key: 'primary', label: 'Primary', description: 'Headings and title-page shapes' },
  { key: 'secondary', label: 'Secondary', description: 'Supporting shapes and dividers' },
  { key: 'accent', label: 'Accent', description: 'Highlights and callouts' },
  { key: 'darkText', label: 'Dark text', description: 'Body copy and dark-on-light text' },
  { key: 'background', label: 'Background', description: 'Page background' },
];

const BULLET_DEMO_ITEMS = [
  'Portfolio positioned in line with the agreed risk profile',
  'No outstanding actions from the previous review',
  'Next review scheduled for the usual cycle',
];

function PalettePreviewDocument({ data }: { data: BrandAssetBuilderData }) {
  const {
    palette,
    headingFont,
    bodyFont,
    logoUrl,
    clientName,
    tableStyle,
    tableBorderColour,
    tableHeaderColour,
    tableHeaderLighten,
  } = data;
  // Mirrors docxModifier.ts's own table-header treatment exactly: the header fill is whatever
  // colour is chosen, lightened only if that's switched on, then a contrast-checked text colour
  // on top - so this preview matches what Generate produces.
  const headerFill = tableHeaderLighten ? lightTint(tableHeaderColour) : tableHeaderColour;
  const headerText = contrastTextColour(headerFill, palette.darkText);
  const isBanded = tableStyle === 'banded';
  // The band is a lighter version of the header fill itself, not an unrelated fixed grey.
  const bandFill = lightTint(headerFill);
  const bulletGlyph = BULLET_STYLE_GLYPHS[data.bulletStyle];

  return (
    <Card
      withBorder
      radius="md"
      p="xl"
      style={{ width: 420, height: 594, backgroundColor: '#ffffff', overflow: 'hidden' }}
    >
      <Stack gap="lg">
        <Group justify="space-between" align="center" wrap="nowrap">
          {logoUrl ? (
            <Image src={logoUrl} alt="Tenant logo" fit="contain" mah={28} maw={100} />
          ) : (
            <Box
              w={90}
              h={28}
              bg="gray.1"
              style={{
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text size="xxs" c="gray.5">
                Company logo
              </Text>
            </Box>
          )}
          <Stack gap={0} align="flex-end">
            <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.7">
              {clientName || 'Client name'}
            </Text>
            <Text size="xxs" c="gray.5">
              Export assets
            </Text>
          </Stack>
        </Group>

        <Divider color={palette.primary} size="sm" />

        <Text size="lg" fw={FONT_WEIGHTS.bold} c={palette.primary} ff={headingFont}>
          Strategic overview
        </Text>

        <Text size="xs" c={palette.darkText} ff={bodyFont} lh={1.6}>
          This section summarises performance across the review period, highlighting the key
          movements in valuation, contributions and overall progress against objectives.
        </Text>

        <Text size="sm" fw={FONT_WEIGHTS.semibold} c={palette.primary} ff={headingFont}>
          1. Key figures
        </Text>

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: bodyFont,
          }}
        >
          <thead>
            <tr style={{ backgroundColor: headerFill }}>
              <th
                style={{
                  border: `1px solid ${tableBorderColour}`,
                  padding: '6px 8px',
                  textAlign: 'left',
                  fontSize: 11,
                  color: headerText,
                }}
              >
                Metric
              </th>
              <th
                style={{
                  border: `1px solid ${tableBorderColour}`,
                  padding: '6px 8px',
                  textAlign: 'left',
                  fontSize: 11,
                  color: headerText,
                }}
              >
                Target
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Revenue', '15% YoY'],
              ['Retention', '98%'],
            ].map(([metric, target], index) => (
              <tr
                key={metric}
                style={{
                  backgroundColor: isBanded && index % 2 === 0 ? bandFill : '#ffffff',
                }}
              >
                <td
                  style={{
                    border: `1px solid ${tableBorderColour}`,
                    padding: '6px 8px',
                    fontSize: 11,
                    color: palette.darkText,
                  }}
                >
                  {metric}
                </td>
                <td
                  style={{
                    border: `1px solid ${tableBorderColour}`,
                    padding: '6px 8px',
                    fontSize: 11,
                    color: palette.darkText,
                  }}
                >
                  {target}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Text size="sm" fw={FONT_WEIGHTS.semibold} c={palette.primary} ff={headingFont}>
          2. Notes
        </Text>

        <Stack gap={4}>
          {BULLET_DEMO_ITEMS.map(item => (
            <Group key={item} gap={8} align="flex-start" wrap="nowrap">
              <Text size="xs" c={data.bulletColour} ff={bodyFont} style={{ flexShrink: 0 }}>
                {bulletGlyph}
              </Text>
              <Text size="xs" c={palette.darkText} ff={bodyFont} lh={1.5}>
                {item}
              </Text>
            </Group>
          ))}
        </Stack>

        <Box
          style={{
            borderLeft: `3px solid ${palette.accent}`,
            paddingLeft: 'var(--mantine-spacing-sm)',
          }}
        >
          <Text size="xs" fs="italic" c={palette.darkText} ff={bodyFont}>
            &ldquo;A clear, consistent presentation of client outcomes across every reporting
            cycle.&rdquo;
          </Text>
        </Box>
      </Stack>
    </Card>
  );
}

export default function ReviewPaletteStep(props: StepProps<BrandAssetBuilderData>) {
  const { data, setData } = props;

  useGoogleFont(data.headingFont);
  useGoogleFont(data.bodyFont);

  const commitPaletteColour = (key: keyof BrandAssetBuilderPalette, value: string) =>
    setData({ ...data, palette: { ...data.palette, [key]: value } });

  const {
    drafts: paletteDrafts,
    draftColour: draftPaletteColour,
    handleFocus: handleFieldFocus,
    handleBlur: handleFieldBlur,
  } = usePaletteColourDrafts(data.palette, commitPaletteColour);

  const tableHeaderColour = useHexColourDraft(data.tableHeaderColour, value =>
    setData({ ...data, tableHeaderColour: value }),
  );
  const tableBorderColour = useHexColourDraft(data.tableBorderColour, value =>
    setData({ ...data, tableBorderColour: value }),
  );
  const bulletColour = useHexColourDraft(data.bulletColour, value =>
    setData({ ...data, bulletColour: value }),
  );

  // "Selected colours" for the border/bullet pickers - the palette's own 5 confirmed brand
  // colours, not the raw ~9-colour logo extraction pool those fields aren't tied to.
  const selectedPaletteColours = [
    data.palette.primary,
    data.palette.secondary,
    data.palette.accent,
    data.palette.darkText,
    data.palette.background,
  ];

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Review palette & fonts"
      description="These colours are seeded from the uploaded logo where extraction succeeds, or a placeholder palette otherwise. Adjust the palette and fonts below - the preview on the left updates live."
      showClientName={false}
      hideProgress
      maxContainerWidth="lg"
    >
      <Group
        align="flex-start"
        gap="xl"
        wrap="nowrap"
        h="100%"
        style={{ alignItems: 'flex-start' }}
      >
        <Box style={{ flexShrink: 0, width: 420, height: '100%' }}>
          <PreviewCanvas nativeWidth={420} nativeHeight={594}>
            <PalettePreviewDocument data={data} />
          </PreviewCanvas>
        </Box>

        <Box flex={1} miw={0} h="100%" style={{ overflow: 'hidden' }}>
          <Tabs
            defaultValue="colours"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Tabs.List>
              <Tabs.Tab value="colours">Colours</Tabs.Tab>
              <Tabs.Tab value="table">Table & lists</Tabs.Tab>
              <Tabs.Tab value="typography">Typography</Tabs.Tab>
            </Tabs.List>

            <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Tabs.Panel value="colours" pt="md">
                <Stack gap="md">
                  <Group justify="flex-end">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() =>
                        setData({ ...data, palette: DEFAULT_BRAND_ASSET_BUILDER_PALETTE })
                      }
                    >
                      Reset to placeholder defaults
                    </Button>
                  </Group>
                  <Stack gap="sm">
                    {PALETTE_FIELDS.map(field => (
                      <PaletteColourField
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        value={paletteDrafts[field.key]}
                        onChange={value => draftPaletteColour(field.key, value)}
                        onFocus={() => handleFieldFocus(field.key)}
                        onBlur={() => handleFieldBlur(field.key)}
                        swatchColours={data.extractedColours}
                        onPickSwatch={hex => commitPaletteColour(field.key, hex)}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="table" pt="md">
                <Stack gap="lg">
                  <PaletteColourField
                    label="Table header colour"
                    value={tableHeaderColour.draft}
                    onChange={tableHeaderColour.setDraft}
                    onFocus={tableHeaderColour.handleFocus}
                    onBlur={tableHeaderColour.handleBlur}
                    swatchColours={selectedPaletteColours}
                    onPickSwatch={hex => setData({ ...data, tableHeaderColour: hex })}
                  />

                  <Switch
                    label="Lighten header colour"
                    description="Tint the header colour above rather than using it at full strength."
                    checked={data.tableHeaderLighten}
                    onChange={event =>
                      setData({ ...data, tableHeaderLighten: event.currentTarget.checked })
                    }
                  />

                  <Switch
                    label="Row banding"
                    description="Alternate row shading, tinted from the header colour. Off uses a plain bordered grid."
                    checked={data.tableStyle === 'banded'}
                    onChange={event =>
                      setData({
                        ...data,
                        tableStyle: event.currentTarget.checked ? 'banded' : 'grid',
                      })
                    }
                  />

                  <PaletteColourField
                    label="Table border colour"
                    value={tableBorderColour.draft}
                    onChange={tableBorderColour.setDraft}
                    onFocus={tableBorderColour.handleFocus}
                    onBlur={tableBorderColour.handleBlur}
                    swatchColours={selectedPaletteColours}
                    onPickSwatch={hex => setData({ ...data, tableBorderColour: hex })}
                  />

                  <Stack gap="sm">
                    <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
                      Bullet shape
                    </Text>
                    <SegmentedControl
                      fullWidth
                      value={data.bulletStyle}
                      onChange={value =>
                        setData({ ...data, bulletStyle: value as BrandAssetBuilderBulletStyle })
                      }
                      data={BULLET_STYLE_OPTIONS}
                    />
                  </Stack>

                  <PaletteColourField
                    label="Bullet colour"
                    value={bulletColour.draft}
                    onChange={bulletColour.setDraft}
                    onFocus={bulletColour.handleFocus}
                    onBlur={bulletColour.handleBlur}
                    swatchColours={selectedPaletteColours}
                    onPickSwatch={hex => setData({ ...data, bulletColour: hex })}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="typography" pt="md">
                <Stack gap="md">
                  <Select
                    label="Heading font"
                    data={HEADING_FONT_OPTIONS}
                    value={data.headingFont}
                    onChange={value => setData({ ...data, headingFont: value ?? data.headingFont })}
                    allowDeselect={false}
                  />
                  <Select
                    label="Body font"
                    data={BODY_FONT_OPTIONS}
                    value={data.bodyFont}
                    onChange={value => setData({ ...data, bodyFont: value ?? data.bodyFont })}
                    allowDeselect={false}
                  />
                </Stack>
              </Tabs.Panel>
            </Box>
          </Tabs>
        </Box>
      </Group>
    </DrawerFlowScreenLayout>
  );
}
